import { useEffect, useRef, useState } from "react";
import type { SessionUser } from "../../../shared/auth/access";
import type { ProductionReferenceData, ProductionRun } from "../models";
import { isSupervisorCriticalDecisionAction } from "../../../../shared/production/supervisor-decisions";
import { productionGateway } from "../production-gateway";
import { isDecisionOutcomeUnknown, submitSupervisorDecision } from "../supervisor-decision-request";
import {
  initialSupervisorDecision, isInitialActionChosen, initialSupervisorSchedule, initialSupervisorIssue,
  requiresCriticalApproval, requiresDecisionNote, isScheduleAction, supervisorDecisionChoices,
  supervisorDecisionSchedule, supervisorDecisionForm, supervisorApprovalScope,
} from "./supervisor-decision-model";
import type { SupervisorDecisionAction } from "./supervisor-decision-model";

export type SupervisorDecisionOptions = {
  run: ProductionRun;
  directAction?: SupervisorDecisionAction;
  reference: ProductionReferenceData;
  user: SessionUser;
  notify: (message: string, tone?: "success" | "error") => void;
  dataIsFresh: () => boolean;
  refreshData: () => void;
  close: () => void;
  submit: (form: Record<string, unknown>, signal: AbortSignal) => Promise<void>;
};

export function useSupervisorDecisionWorkspace({ run, directAction, reference, user, notify, dataIsFresh, close, submit }: SupervisorDecisionOptions) {
  const [action, setAction] = useState<SupervisorDecisionAction>(() => initialSupervisorDecision(run, reference, user, directAction, Date.now()));
  const [actionChosen, setActionChosen] = useState(isInitialActionChosen(user.role, directAction));
  const [note, setNote] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);
  const [otherActionsOpen, setOtherActionsOpen] = useState(false);
  const [operatorId, setOperatorId] = useState(run.assignedOperatorId ?? "");
  const [lineId, setLineId] = useState(run.lineId);
  const initialSchedule = initialSupervisorSchedule(action, Date.now());
  const [shiftDate, setShiftDate] = useState(initialSchedule.shiftDate);
  const [shiftCode, setShiftCode] = useState(initialSchedule.shiftCode);
  const [transferTime, setTransferTime] = useState(initialSchedule.time);
  const initialTransferMinutes = Math.max(1, Math.round((Date.parse(run.plannedEndAt ?? "") - Date.parse(run.plannedStartAt ?? "")) / 60_000 || 60));
  const [transferHourCount, setTransferHourCount] = useState(String(Math.floor(initialTransferMinutes / 60)));
  const [transferMinuteCount, setTransferMinuteCount] = useState(String(initialTransferMinutes % 60));
  const [saving, setSaving] = useState(false);
  const request = useRef<AbortController | null>(null);
  const decisionInFlight = useRef(false);
  const dataIsFreshRef = useRef(dataIsFresh);
  dataIsFreshRef.current = dataIsFresh;
  const [outcomeUnknown, setOutcomeUnknown] = useState(false);
  useEffect(() => () => { request.current?.abort(); }, []);
  const [supervisorPin, setSupervisorPin] = useState("");
  const [formError, setFormError] = useState("");
  const [savingStep, setSavingStep] = useState<"approval" | "decision" | null>(null);
  const issue = initialSupervisorIssue(run, Date.now());
  const existingIssue = issue.existingIssue;
  const [issueCategory, setIssueCategory] = useState(issue.category);
  const [issueSubreason, setIssueSubreason] = useState(issue.subreason);
  const [issueDescription, setIssueDescription] = useState(issue.description);
  const [issueStartedAt] = useState(issue.startedAt);
  const [issueSource] = useState(issue.source);
  const [responsibleTeam, setResponsibleTeam] = useState(issue.responsibleTeam);
  const [issueStatus, setIssueStatus] = useState(issue.status);
  const criticalApprovalRequired = requiresCriticalApproval(action);
  const noteRequired = requiresDecisionNote(action);
  const scheduleAction = isScheduleAction(action);
  const { quickActions, otherActions } = supervisorDecisionChoices(run, user.role, Date.now());
  const chooseAction = (value: SupervisorDecisionAction) => {
    setAction(value); setActionChosen(true); setFormError("");
    const schedule = supervisorDecisionSchedule(value, run, Date.now());
    if (schedule) { setShiftDate(schedule.shiftDate); setShiftCode(schedule.shiftCode); setTransferTime(schedule.time); }
  };
  const dismiss = () => {
    if (decisionInFlight.current) notify("Kararın sonucu henüz doğrulanmadı. Tekrar işlem yapmadan önce güncel kaydı ve iş geçmişini kontrol edin.");
    close();
  };
  const saveDecision = async () => {
    if (request.current || outcomeUnknown) return;
    if (user.role === "VARDIYA_AMIRI" && !isSupervisorCriticalDecisionAction(action)) { setFormError("İş emrini operatör terminalden başlatır. Kritik bir düzeltme veya onay seçin."); return; }
    if (!dataIsFreshRef.current()) { setFormError("Güncel bilgiler alınamadı. İşlemi başlatmadan önce verileri yenileyin."); return; }
    const controller = new AbortController();
    request.current = controller;
    setSaving(true);
    setFormError("");
    setSavingStep(criticalApprovalRequired ? "approval" : "decision");
    let decisionStarted = false;
    try {
      const form = supervisorDecisionForm(run, reference, {
        action, note, operatorId, lineId, shiftDate, shiftCode, transferTime, transferHourCount, transferMinuteCount,
        issueCategory, issueSubreason, issueDescription, issueStartedAt, issueSource, responsibleTeam, issueStatus,
      });
      const approvalScope = supervisorApprovalScope(action);
      await submitSupervisorDecision({
        signal: controller.signal,
        isFresh: () => dataIsFreshRef.current(),
        approve: criticalApprovalRequired ? async () => (await productionGateway.approveCriticalAction({ supervisorId: user.id, supervisorName: user.name, pin: supervisorPin, actionScope: approvalScope, targetRef: run.id }, controller.signal)).approvalToken : undefined,
        onApproved: (token) => { form.approvalToken = token; setSupervisorPin(""); },
        onDecisionStart: () => { decisionStarted = true; decisionInFlight.current = true; setSavingStep("decision"); },
        decide: () => submit(form, controller.signal),
      });
    } catch (error) {
      if (controller.signal.aborted) return;
      const unknown = decisionStarted && isDecisionOutcomeUnknown(error);
      const message = unknown ? "Kararın sonucu doğrulanamadı. Tekrar işlem yapmadan önce pencereyi kapatıp güncel kaydı ve iş geçmişini kontrol edin." : error instanceof Error ? error.message : "İşlem tamamlanamadı.";
      if (unknown) setOutcomeUnknown(true);
      setFormError(message);
      notify(message);
    } finally {
      decisionInFlight.current = false;
      if (request.current === controller) request.current = null;
      if (!controller.signal.aborted) { setSaving(false); setSavingStep(null); }
    }
  };
  return { action, actionChosen, note, setNote, noteOpen, setNoteOpen, otherActionsOpen, setOtherActionsOpen, operatorId, setOperatorId, lineId, setLineId, shiftDate, setShiftDate, shiftCode, setShiftCode, transferTime, setTransferTime, transferHourCount, setTransferHourCount, transferMinuteCount, setTransferMinuteCount, saving, outcomeUnknown, supervisorPin, setSupervisorPin, formError, setFormError, savingStep, existingIssue, issueCategory, setIssueCategory, issueSubreason, setIssueSubreason, issueDescription, setIssueDescription, issueStartedAt, issueSource, responsibleTeam, setResponsibleTeam, issueStatus, setIssueStatus, criticalApprovalRequired, noteRequired, scheduleAction, quickActions, otherActions, chooseAction, saveDecision, dismiss };
}
